import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, Renderer2, inject } from '@angular/core';
import { BoundingBox } from '@newroom-connect/library/types';

export type DragPositionEvent = {
  top: number;
  left: number;
};

@Directive({
  selector: '[nrcDragAndDrop]',
  standalone: true
})
export class DragDropDirective {
  @Input({ required: true }) public targetContainerRef!: ElementRef<HTMLElement>;
  @Input({ required: true }) public boundingBox!: BoundingBox;
  @Input() public isDragDisabled = false;

  @Output() public dragPositionChanged = new EventEmitter<DragPositionEvent>();

  private startX = 0;
  private startY = 0;
  private initialTop = 0;
  private initialLeft = 0;
  private isDraggable = false;

  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  /**
   * @constructor
   */
  constructor() {
    this.onMousemove = this.onMousemove.bind(this);
    this.onMouseup = this.onMouseup.bind(this);
  }

  /**
   * Handle mouse down event to start dragging.
   * @param event - Mouse event.
   */
  @HostListener('mousedown', ['$event'])
  public onMousedown(event: MouseEvent): void {
    if (this.isDragDisabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.startX = event.clientX;
    this.startY = event.clientY;

    // Use current bounding box position as initial values
    this.initialTop = this.boundingBox.top;
    this.initialLeft = this.boundingBox.left;

    this.isDraggable = true;

    this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'absolute');
    this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'none');

    document.addEventListener('mousemove', this.onMousemove);
    document.addEventListener('mouseup', this.onMouseup);
  }

  /**
   * Handle mouse move event during dragging.
   * @param event - Mouse event.
   */
  public onMousemove(event: MouseEvent): void {
    if (!this.isDraggable || this.isDragDisabled) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    const containerWidth = this.targetContainerRef.nativeElement.offsetWidth;
    const containerHeight = this.targetContainerRef.nativeElement.offsetHeight;

    // Calculate new position as percentages
    const newTop = this.initialTop + (100 * deltaY / containerHeight);
    const newLeft = this.initialLeft + (100 * deltaX / containerWidth);

    // Update element styles
    this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${newTop}%`);
    this.renderer.setStyle(this.elementRef.nativeElement, 'left', `${newLeft}%`);

    // Emit position change event
    this.dragPositionChanged.emit({ top: newTop, left: newLeft });
  }

  /**
   * Handle mouse up event to end dragging.
   */
  public onMouseup(): void {
    if (this.isDraggable) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'auto');

      document.removeEventListener('mousemove', this.onMousemove);
      document.removeEventListener('mouseup', this.onMouseup);
    }

    this.isDraggable = false;
  }
}
